// Fill-in fields inside a drafted document — the frontend mirror of
// backend/utils/smartFields.js. Keep the two grammars identical: the backend
// rejects approval of any document still containing a {{…}} placeholder, and
// this is what lets the editor show the same list before the doctor clicks
// Approve. See docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2 C11.
//
// No imports from the rest of the app, so it can be compiled and checked in
// isolation.

export type SmartFieldKind = 'date' | 'text' | 'number' | 'choice';

export interface SmartField {
  kind: SmartFieldKind;
  label: string;
  unit?: string;
  options?: string[];
}

const FIELD_KINDS: SmartFieldKind[] = ['date', 'text', 'number', 'choice'];

/** Any {{…}} on one line — broader than the four kinds, like the backend guard. */
export const PLACEHOLDER_RE = /\{\{([^{}\n]{1,200})\}\}/g;

/** `inner` is the text between the braces; null if it isn't a well-formed field. */
export function parseSmartField(inner: string): SmartField | null {
  const parts = inner.split('|').map((p) => p.trim());
  const kind = (parts[0] || '').toLowerCase() as SmartFieldKind;
  const label = parts[1] || '';
  if (!FIELD_KINDS.includes(kind) || !label) return null;

  const field: SmartField = { kind, label };
  if (kind === 'number' && parts[2]) field.unit = parts[2];
  if (kind === 'choice') {
    const options = (parts[2] || '').split(';').map((o) => o.trim()).filter(Boolean);
    if (options.length < 2) return null;
    field.options = options;
  }
  return field;
}

/** The inner text of a raw placeholder such as "{{date|Follow-up date}}". */
export function innerOf(raw: string): string {
  return raw.replace(/^\{\{/, '').replace(/\}\}$/, '');
}

/** Labels of every placeholder still in `text` — what blocks approval. */
export function unfilledFieldLabels(text: string): string[] {
  const labels: string[] = [];
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    const field = parseSmartField(match[1] ?? '');
    labels.push(field ? field.label : match[0]);
  }
  return labels;
}

/** Removes every placeholder (and a space it leaves doubled). */
export function removeAllPlaceholders(text: string): string {
  return text.replace(PLACEHOLDER_RE, '').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+$/gm, '');
}

// ── Dates ───────────────────────────────────────────────────────
// "14 Sep 2026" — the format used across the app and on generated sheets
// (backend/utils/prescriptionSheet.js). Fixed month names: Intl's short month
// prints "Sept" on some ICU builds.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDocDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Parses the app's own format or an ISO yyyy-mm-dd string; null otherwise. */
export function parseDocDate(text: string): Date | null {
  const own = /^(\d{1,2}) ([A-Za-z]{3})[a-z]* (\d{4})$/.exec(text.trim());
  if (own) {
    const month = MONTHS.findIndex((m) => m.toLowerCase() === own[2]!.toLowerCase());
    if (month >= 0) return new Date(Number(own[3]), month, Number(own[1]));
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return null;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  // Clamp to the last day of the target month (31 Jan + 1 month → 28/29 Feb).
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
}

/** Quick picks offered for every date field and date button. */
export const DATE_QUICK_PICKS: { label: string; make: (today: Date) => Date }[] = [
  { label: 'Today', make: (t) => t },
  { label: '+3 days', make: (t) => addDays(t, 3) },
  { label: '+1 week', make: (t) => addDays(t, 7) },
  { label: '+2 weeks', make: (t) => addDays(t, 14) },
  { label: '+1 month', make: (t) => addMonths(t, 1) },
];

/** "28 Sep 2026", "05/09/2026", "5-9-26" — dates the editor offers to change in place. */
export const DATE_IN_TEXT_RE = /\b(\d{1,2} (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{4}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/g;

/** A label that asks for a date but has nothing after its colon yet. */
export const EMPTY_DATE_LABEL_RE = /(?:^|\s)(follow[- ]?up(?: date)?|review(?: on| date)?|next visit|revisit|date)\s*:\s*$/i;
